<svelte:options
  customElement={{
    tag: "oc-cinema-v1",
    shadow: "none",
    /*                                            */
    extend: window.__components.extend({ delegateFocus: true }),
    props: {
      arrowButtonPosition: { type: "String", attribute: "arrow-button-position" },
      ocAriaLabel: { type: "String", attribute: "oc-aria-label" },
      cutItems: { type: "Boolean", attribute: "cut-items" },
      stretchItems: { type: "Boolean", attribute: "stretch-items" },
      paginationDots: { type: "Boolean", attribute: "pagination-dots" },
    },
  }}
/>

<script lang="ts">
  import { onMount } from "svelte";
  import type { Props } from "./CinemaV1.types";
  import PaginationDotsV1 from "../../../common/components/PaginationDotsV1/PaginationDotsV1.svelte";

  type SlideHTMLElement = {
    slideIsVisible?: boolean;
  } & HTMLElement;

  export let host: HTMLElement;
  export let arrowButtonPosition: Exclude<Props["arrowButtonPosition"], undefined>;
  export let ocAriaLabel: Props["ocAriaLabel"] = undefined;
  export let cutItems: Props["cutItems"] = false;
  export let stretchItems: Props["stretchItems"] = false;
  export let paginationDots: Props["paginationDots"] = false;

  let cinemaStage: HTMLElement;
  let currentSlide = 0;

  const slides = Array.from(host.children) as SlideHTMLElement[];

  /*                                                        */
  let hideLeftArrow = true;
  let hideRightArrow = true;

  let leftButton: boolean = false;
  let rightButton: boolean = true;
  let leftButtonTimer: number | undefined = undefined;
  let rightButtonTimer: number | undefined = undefined;

  const cinemaStageGap = parseInt(getComputedStyle(host).getPropertyValue("--gap"), 10);

  const getSlidesPerPage = () => {
    return parseInt(getComputedStyle(host).getPropertyValue("--items-per-screen"), 10);
  };

  const moveTo = (targetPage: number) => {
    const slidesPerPage = getSlidesPerPage();
    const targetSlideIndex = targetPage * slidesPerPage;
    const targetSlide = slides[targetSlideIndex];
    const { width } = targetSlide.getBoundingClientRect();

    if (cutItems) {
      cinemaStage.scrollLeft =
        targetSlide.offsetLeft - cinemaStageGap - cinemaStageGap - 0.25 * width;
    } else {
      cinemaStage.scrollLeft = targetSlide.offsetLeft - cinemaStageGap - cinemaStageGap;
    }
  };

  onMount(() => {
    const intersectionObserverOptions: IntersectionObserverInit = {
      root: cinemaStage,
      threshold: [0.999, 1],
    };

    let lastIntersectingSlide: SlideHTMLElement | null = null;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const target = entry.target as SlideHTMLElement;

        target.slideIsVisible = entry.isIntersecting;
        if (entry.isIntersecting) {
          lastIntersectingSlide = target;
        }

        clearTimeout(leftButtonTimer);
        clearTimeout(rightButtonTimer);

        if (cinemaStage.scrollLeft <= cinemaStageGap) {
          hideLeftArrow = true;
          leftButtonTimer = setTimeout(() => {
            leftButton = false;
          }, 1000) as unknown as number;
        } else {
          leftButton = true;
          hideLeftArrow = false;
        }

        if (
          cinemaStage.scrollLeft >=
          cinemaStage.scrollWidth - cinemaStage.clientWidth - cinemaStageGap
        ) {
          hideRightArrow = true;
          rightButtonTimer = setTimeout(() => {
            rightButton = false;
          }, 1000) as unknown as number;
        } else {
          rightButton = true;
          hideRightArrow = false;
        }
      });
      currentSlide = slides.findIndex((slide) => slide.slideIsVisible);
      if (currentSlide === -1) {
        /*                                                              */
        currentSlide = slides.findIndex((slide) => slide === lastIntersectingSlide);
      }
    }, intersectionObserverOptions);

    slides.forEach((slide) => observer.observe(slide));

    return () => {
      slides.forEach((slide) => observer.unobserve(slide));
    };
  });
</script>

<div class="cinema">
  <section
    class="cinema__stage"
    class:cinema__stage--cut-items={cutItems}
    class:cinema__stage--stretch-items={stretchItems}
    bind:this={cinemaStage}
    aria-label={ocAriaLabel}
  >
    <slot />
  </section>
  <oc-icon-button-v1
    class="cinema__arrow cinema__arrow--left"
    class:cinema__arrow--hidden={hideLeftArrow}
    class:cinema__arrow--removed={leftButton === false}
    style="--arrow-button-position:{arrowButtonPosition}"
    iconType="arrow-left"
    on:click={() => moveTo(Math.round(currentSlide / getSlidesPerPage()) - 1)}
    aria-hidden="true"
    elevated
    tabindex="-1"
    disabled={hideLeftArrow === true}
  ></oc-icon-button-v1>
  <oc-icon-button-v1
    class="cinema__arrow cinema__arrow--right"
    class:cinema__arrow--hidden={hideRightArrow}
    class:cinema__arrow--removed={rightButton === false}
    style="--arrow-button-position:{arrowButtonPosition}"
    iconType="arrow-right"
    on:click={() => moveTo(Math.round(currentSlide / getSlidesPerPage()) + 1)}
    aria-hidden="true"
    elevated
    tabindex="-1"
    disabled={hideRightArrow === true}
  ></oc-icon-button-v1>
</div>

{#if paginationDots}
  <PaginationDotsV1
    amountDots={Math.round(slides.length / getSlidesPerPage())}
    activeDot={Math.round(currentSlide / getSlidesPerPage())}
    {moveTo}
  />
{/if}

<style lang="scss" global>
  @use "../../../common/scss/mixins/mixins";
  @use "@otto-ec/design-tokens/component" as tokens;

  :host {
    @include mixins.no-tap-highlight();
    display: block;
    --items-per-screen: 4;
    --gap: #{tokens.$oc-component-carousel-stage-gap-x}; /*                                                       */
  }

  .cinema__stage {
    ::slotted(*) {
      $base: calc(
        (100% - (var(--items-per-screen) + 1) * var(--gap)) /
          (var(--cut-item-visible-part, 0) + var(--items-per-screen))
      );

      flex: 1 0 $base;
      max-width: $base;
    }

    &--cut-items {
      --cut-item-visible-part: #{tokens.$oc-component-cinema-cut-item-visible-percentage};
    }

    &--stretch-items {
      ::slotted(*) {
        max-width: none;
      }
    }
  }

  @media (max-width: 1000px) {
    :host {
      --items-per-screen: 3;
    }
  }

  @media (max-width: 750px) {
    :host {
      --items-per-screen: 2;
    }
  }

  @media (max-width: 500px) {
    :host {
      --items-per-screen: 1;
    }
  }

  .cinema {
    position: relative;

    &__stage {
      display: flex;
      gap: var(--gap);
      overflow-x: auto;
      overflow-y: hidden;
      scrollbar-width: none; /*     */
      padding: 4px 0;
      margin: -4px 0;

      &::-webkit-scrollbar {
        display: none; /*                */
      }

      scroll-behavior: smooth;

      /*                                            */
      @media (prefers-reduced-motion) {
        scroll-behavior: auto;
      }

      &:before {
        content: "";
        flex: 0 0 0px;
      }
      &:after {
        content: "";
        flex: 0 0 0px;
      }
    }

    /*          */
    @media (hover: none) and (pointer: coarse) {
      &__arrow {
        display: none;
      }
    }

    /*                                                 */
    @media (hover: hover) and (pointer: fine), (hover: none) and (pointer: fine) {
      &__arrow {
        position: absolute;
        top: var(--arrow-button-position, 50%);
        transform: translate(0, -50%);

        &--hidden {
          opacity: 0;
        }

        &--removed {
          @include mixins.visually-hidden();
        }

        &--left {
          left: 8px;
        }

        &--right {
          right: 8px;
        }
      }
    }
  }
</style>
